import React, { ChangeEvent, useEffect, useRef , useState } from 'react';
import { FormControl, FormHelperText, InputBaseProps, InputLabel } from '@material-ui/core';
import { get, getOr, isNaN, isNumber } from 'lodash/fp';
import { FieldProps, Field as FormikField, FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { Input as BaseInput } from '../../../../../../../ui-kit/TextField/TextField';
import { validateMaxDecimals } from '../../../../../../../utils';
import { TooltipedInfoIcon } from '../../../../../../../ui-kit';

export interface NumberInputFieldChangeEvent {
    target: {
        name: string;
        value: number;
    };
}

export type CombinedProps = FieldProps<number>['field'] & TextFieldProps;

interface NumberInputFieldProps<T> extends TextFieldProps {
    name: string;
    formikProps: FormikProps<T>;
}

const InputFieldWithCallback = <T,>({
    name, label, formikProps: { errors, touched, handleChange }, ...other
}: NumberInputFieldProps<T>) => {
    const { t } = useTranslation();
    return (
        <FormikField
            id={name}
            name={name}
            label={label}
            as={InputWithCallback}
            error={Boolean(get(name, errors)) && get(name, touched)}
            helpertext={get(name, touched) ? t(getOr(' ', name, errors)) : ' '}
            variant="outlined"
            onChange={handleChange}
            {...other}
        />
    );
};

export interface TextFieldProps extends Omit<InputBaseProps, 'onChange' | 'value'> {
    select?: boolean;
    label: string;
    helpertext?: string;
    tooltip?: string;
    children?: React.ReactNode | React.ReactNode[];
    onChangeCallback(e: NumberInputFieldChangeEvent): void;
    initialValue?: number;
    maxDecimals?: number;
}

const InputWithCallback = React.forwardRef(({
    label,
    id,
    select,
    value: formikValue,
    name,
    className,
    required,
    helpertext,
    error,
    margin,
    tooltip,
    onChange,
    onChangeCallback,
    initialValue = 0,
    maxDecimals = 2,
    ...other
}: CombinedProps, forwardRef) => {
    const [value, setValue] = useState<number | undefined>(initialValue);
    const previousFormikValue = useRef(formikValue);

    useEffect(() => {
        if (previousFormikValue.current !== formikValue){
            setValue(formikValue);
            previousFormikValue.current = formikValue;
        }
    }, [formikValue]);

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newValue = parseFloat(event.target.value);
        const validatedValue = validateMaxDecimals({ value: newValue, maxDecimals });

        if (isNaN(validatedValue)) {
            setValue(undefined);
            return;
        }

        const customEvent = { target: { name, value: validatedValue } };

        if (validatedValue === 0 && isNumber(validatedValue)) {
            setValue(validatedValue);
            onChange(customEvent);
            onChangeCallback(customEvent);
            return;
        }

        if (validatedValue > 0) {
            setValue(validatedValue);
            onChange(customEvent);
            onChangeCallback(customEvent);
        }
    };

    const textFieldLabel = label && (
        <InputLabel style={{ marginLeft: '6px' }} shrink htmlFor={id} error={!!error}>
            {label}
            {tooltip && (
                <TooltipedInfoIcon tooltip={tooltip} tooltipFontSize='1rem' />
            )}
        </InputLabel>
    );

    return (
        <FormControl fullWidth className={className} margin={margin || 'normal'} required={required}>
            {textFieldLabel}
            <BaseInput
                onChange={handleChange}
                name={name}
                value={value}
                ref={forwardRef}
                id={id}
                {...other}
            />
            <FormHelperText error={!!error}>{helpertext}</FormHelperText>
        </FormControl>
    );
});

export default InputFieldWithCallback;
